/* eslint-disable react/no-unescaped-entities */
import clsx from 'clsx';
import Image from 'next/image';
import React from 'react';
import toast from 'react-hot-toast';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import config from '../../config';
import { useAllKitties } from '../../hooks/useAllKitties';
import { useBlocksStore } from '../../hooks/useBlocksStore';
import { useCityBuilder } from '../../hooks/useCityBuilder';
import { useMyCities } from '../../hooks/useMyCities';
import { useUserTraits } from '../../hooks/userUserTraits';
import calculateCityRating from '../../utils/calculateCityRating';
import calculateMaxPopulation from '../../utils/calculateMaxPopulation';
import formatNumber from '../../utils/formatNumber';
import Alert from '../Alert';
import Button from '../Button';
import Modal from '../Modal';
import ModalContent from '../ModalContent';
import Tag from '../Tag';
import TraitImage, { getCollectionByGeneration } from '../TraitImage';
import BlockSelector from './BlockSelector';
import CityImage from './CityImage';

const CityBuilderCityEditor = () => {
  const { allCities } = useMyCities();
  const {
    blocksInCity,
    originalBlocksInCity,
    selectedCity,
    saveCity,
    resetCity,
    numberOfTxns,
    originalUpgrade,
    currentUpgrade,
    setCurrentUpgrade,
    currentUpgradeUrl,
  } = useCityBuilder();

  const [upgradeModalOpen, setUpgradeModalOpen] = React.useState(false);

  const { allKitties } = useAllKitties();

  const numberOfFilledBlocks = blocksInCity.filter((block) => block.block).length;
  const maxPopulation = calculateMaxPopulation({ city: selectedCity, blockCount: numberOfFilledBlocks });

  const updatedMetadata = {
    'Block Count': blocksInCity.filter((block) => block.block)?.length || 0,
  };
  for (const block of blocksInCity.filter((block) => block.block)) {
    updatedMetadata[`Block #${block.index}`] = `${block.block.params.name.split(' - ')[1]} - ${block.block.index}`;
    updatedMetadata[`Block #${block.index} Rarity`] = block.block.metadata.properties.Rarity;
  }
  const rarityScore = calculateCityRating({ ...selectedCity, metadata: { ...selectedCity.metadata, properties: updatedMetadata } }, allCities);
  const cityName = selectedCity.details?.name || selectedCity.params.name;

  const hasChanged =
    JSON.stringify(originalBlocksInCity) !== JSON.stringify(blocksInCity) || JSON.stringify(originalUpgrade) !== JSON.stringify(currentUpgrade);

  const onUpgradeCitySelect = (_upgrade) => {
    if (numberOfFilledBlocks !== 9) {
      toast.error('You must have 9 blocks in your city to Upgrade.');
      return;
    }
    setCurrentUpgrade(_upgrade);
    setUpgradeModalOpen(false);
  };

  return (
    <div className='flex flex-col gap-8 mx-auto w-full'>
      <div className='flex sm:justify-end gap-4 w-full'>
        <div className='flex flex-col sm:flex-row sm:justify-end gap-4 w-full'>
          {hasChanged && (
            <Button theme='outline-gray' onClick={resetCity}>
              Reset Changes
            </Button>
          )}
          <Button theme='outline' className='!px-6' onClick={() => setUpgradeModalOpen(true)}>
            <span className='w-5 h-5 mr-3'>
              <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 576 512'>
                <path
                  d='M352 32H224V66.3 176L159.6 85.8C84.1 121.8 32 198.8 32 288v64H544l0-66.4c-.9-88.1-52.8-164.1-127.5-199.8L352 176V66.3 32zM0 408s80 72 288 72s288-72 288-72V384H0v24z'
                  fill='currentColor'
                />
              </svg>
            </span>
            <span>Upgrade City {process.env.NEXT_PUBLIC_CITY_UPGRADES_ARE_LIVE !== 'true' ? '(Preview)' : ''}</span>
          </Button>
          <Button disabled={!hasChanged} onClick={saveCity}>
            Next
          </Button>
        </div>
      </div>

      <div className='mx-auto w-full'>
        <div className='w-full grid grid-cols-12 gap-8'>
          <div className='relative overflow-hidden flex flex-col gap-12 col-span-12 lg:col-span-6'>
            <div className='bg-gray-900 transition duration-300 block w-full rounded-lg overflow-hidden'>
              <CityImage blocks={blocksInCity} cityImageUrl={currentUpgradeUrl} />

              {selectedCity && (
                <div className='flex-1 p-6 text-left flex flex-col gap-3 xl:gap-0 xl:flex-row justify-between group-hover:bg-gray-700 transition duration-150'>
                  <div>
                    {selectedCity.details?.name && <p className='text-gray-500 text-sm'>{selectedCity.params.name}</p>}
                    <p className='font-medium text-lg'>{cityName}</p>
                    <p className='text-gray-500 mt-1'>{selectedCity.index}</p>
                  </div>

                  <div className='flex gap-4 items-center'>
                    <p className='text-gray-500 flex flex-col gap-1'>
                      <span>Population</span>
                      <span className='text-xl font-bold text-white'>
                        {selectedCity.currentPopulation}/{maxPopulation}
                      </span>
                    </p>
                    <p className='text-gray-500 flex flex-col gap-1'>
                      <span>Blocks</span>
                      <span className='text-xl font-bold text-white'>{numberOfFilledBlocks}/9</span>
                    </p>
                    <p className='text-gray-500 flex flex-col items-center justify-center gap-1'>
                      <span>Rarity Score</span>
                      <span className='text-xl font-bold text-white'>{rarityScore}</span>
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className='grid grid-cols-2 lg:grid-cols-3 gap-4 w-full lg:col-span-6 col-span-12'>
            {blocksInCity.map((block) => (
              <BlockSelector key={block.index} cityBlockIndex={block.index} weight={block.weight} activeBlock={block.block} />
            ))}
          </div>
        </div>

        <Modal open={upgradeModalOpen} onClose={setUpgradeModalOpen} type='large'>
          <ModalContent
            heading='Upgrade your Shitty City'
            description='Spend $TREATS and $ALGO to Upgrade your Shitty City as many times as you want.'
            className='!p-2 !pt-16 md:!p-5'
          >
            {numberOfFilledBlocks < 9 && (
              <div className='max-w-md mx-auto'>
                <Alert type='danger'>You must have 9 blocks in your city to Upgrade.</Alert>
              </div>
            )}
            <div className='flex md:flex-row flex-col gap-4 w-full md:overflow-scroll px-2 py-3'>
              {config.cityUpgrades.map((upgrade) => (
                <CityUpgradePreview
                  key={upgrade.imageUrlClient}
                  upgrade={upgrade}
                  active={currentUpgradeUrl === upgrade.imageUrlClient}
                  onClick={onUpgradeCitySelect}
                  allKitties={allKitties}
                  allCities={allCities}
                />
              ))}
            </div>
          </ModalContent>
        </Modal>
      </div>
    </div>
  );
};

const CityUpgradePreview = ({ upgrade, active, onClick, allKitties, allCities }) => {
  const { name, imageUrlClient, treatsCost, algoCost, requirements } = upgrade;
  const { blocksInCity, selectedCity } = useCityBuilder();
  const filledBlocks = blocksInCity.filter((block) => block.block);
  const numberOfFilledBlocks = filledBlocks.length;
  const filledBlocksNames = filledBlocks.map((block) => block.block.params.name.split(' - ')[1]);
  const { allBlocks } = useBlocksStore();

  const cityCitizenIds = selectedCity?.citizens ?? [];
  const cityCitizens = allKitties.filter((citizen) => cityCitizenIds.includes(citizen.id));

  const { allTraits, ownedTraits } = useUserTraits(cityCitizens);
  const ownedTraitNames = ownedTraits.map((t) => t.value);

  const hasCorrectBlocks =
    !upgrade.requirements?.blocks || upgrade.requirements?.blocks.filter((block) => filledBlocksNames.includes(block)).length >= 1;

  const ownedRequiredTraits = requirements?.traits
    ? requirements.traits
        .map((trait) => ({
          trait,
          owned: ownedTraitNames.some((t) => t === trait),
        }))
        .sort((a, b) => (a.owned ? -1 : 1))
    : [];
  const hasCorrectTraits = requirements?.traits ? ownedRequiredTraits.filter((t) => t.owned).length >= 1 : true;
  const hasEnoughCitizens = (selectedCity?.currentPopulation || 0) >= 26;

  const citiesWithUpgrades = allCities.filter((city) => city?.metadata?.properties['Upgrade'] === name);
  const upgradeCount = citiesWithUpgrades.length;
  const upgradeLimit = upgrade.limit;
  const upgradeLimitReached = upgradeCount >= upgradeLimit;
  const isEnabled = numberOfFilledBlocks === 9 && hasCorrectBlocks && hasCorrectTraits && hasEnoughCitizens && !upgradeLimitReached;

  return (
    <button
      type='button'
      className={clsx(
        'flex-shrink-0 w-full md:w-[450px] flex flex-col rounded-md p-3',
        active && 'ring-2 ring-primary-500',
        !isEnabled && 'cursor-not-allowed'
      )}
      disabled={!isEnabled}
      onClick={() => onClick(upgrade)}
    >
      <h3 className='text-gray-200 mb-2 text-left text-xl font-medium'>{name}</h3>

      <div className='relative'>
        {upgradeLimitReached && (
          <div className='absolute inset-0 z-20 flex items-center justify-center bg-gray-900/75'>
            <p className='text-white text-3xl font-medium uppercase'>Sold out!</p>
          </div>
        )}
        <div className={clsx('z-10 aspect-[50/35] w-full relative rounded-md overflow-hidden', !isEnabled && 'grayscale')}>
          <Image src={imageUrlClient} alt='City Preview' className='absolute inset-0 w-full h-full' width={1000} height={700} />
        </div>
      </div>

      <div className={clsx('rounded-md p-2 mt-2 flex flex-col gap-2 w-full')}>
        <div className='flex gap-6 items-end'>
          <div className='text-white text-sm text-left flex flex-col gap-1'>
            <span className='text-gray-400'>Cost</span>
            <div className='flex items-center gap-3'>
              <Tag className='flex items-center !flex-row gap-1.5'>
                <img src='/treats-emoji.webp' alt='Shitty Treats' className='w-3 inline-block' />
                <span className='font-medium'> {formatNumber(treatsCost)}</span>
              </Tag>
              <Tag className='flex items-center !flex-row gap-1.5 mt-1'>
                <svg id='Layer_1' data-name='Layer 1' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 113 113.4' className='w-3'>
                  <polygon
                    points='19.6 113.4 36 85 52.4 56.7 68.7 28.3 71.4 23.8 72.6 28.3 77.6 47 72 56.7 55.6 85 39.3 113.4 58.9 113.4 75.3 85 83.8 70.3 87.8 85 95.4 113.4 113 113.4 105.4 85 97.8 56.7 95.8 49.4 108 28.3 90.2 28.3 89.6 26.2 83.4 3 82.6 0 65.5 0 65.1 0.6 49.1 28.3 32.7 56.7 16.4 85 0 113.4 19.6 113.4'
                    fill='currentColor'
                  />
                </svg>
                <span className='font-medium'> {formatNumber(algoCost)}</span>
              </Tag>
            </div>
          </div>

          <div className='text-white text-sm text-left flex flex-col gap-1'>
            <span className='text-gray-400'>Max Upgrades</span>
            <div className='flex items-center gap-3'>
              <Tag className='flex items-center !flex-row gap-1.5'>
                <span className='font-medium'>
                  {upgradeCount}/{formatNumber(upgrade.limit)}
                </span>
              </Tag>
            </div>
          </div>
        </div>

        <>
          <div>
            <div className='text-white text-sm text-left flex flex-col gap-1 mb-3'>
              <span className='text-gray-400'>Requirements</span>
              <div className='flex gap-3'>
                <Tag className='flex items-center gap-1.5'>
                  <span className={clsx(filledBlocks.length === 9 ? 'text-green-400' : 'text-red-400')}>{filledBlocks.length}/9 blocks</span>
                </Tag>
                <Tag className='flex items-center gap-1.5'>
                  <span className={clsx(hasEnoughCitizens ? 'text-green-400' : 'text-red-400')}>
                    {selectedCity?.currentPopulation || 0}/26 shitizens
                  </span>
                </Tag>
              </div>
            </div>

            <div className='text-left text-sm text-white flex flex-col overflow-hidden w-full'>
              <span className='text-gray-400'>Requires 1 of these blocks in the city</span>{' '}
              {requirements?.blocks ? (
                <div className='flex gap-x-3 gap-y-1 overflow-x-scroll mt-2 p-1'>
                  {requirements.blocks
                    .sort((a, b) => (filledBlocksNames.includes(a) ? -1 : 1))
                    .map((block) => {
                      const blockData = allBlocks.find((b) => b.params.name.includes(block));
                      const rarity = blockData?.metadata?.properties?.Rarity;

                      return (
                        <div key={block} className='relative'>
                          <div
                            className={clsx(
                              'relative w-[75px] h-[75px] ring-2 rounded-md shrink-0',
                              filledBlocksNames.includes(block) ? 'ring-green-400' : 'ring-red-400'
                            )}
                          >
                            <LazyLoadImage
                              src={`/blocks/minified/${blockData.index}.png`}
                              title={blockData.params.name}
                              alt={blockData.name}
                              width={400}
                              height={400}
                              className={clsx(
                                'absolute inset-0 object-cover w-full h-full rounded-md',
                                !filledBlocksNames.includes(block) && 'grayscale'
                              )}
                            />
                            {/* <div className='absolute bottom-1 right-0 left-0 flex justify-center'>
                          </div> */}
                          </div>
                          <div className='mt-3'>
                            <Tag className='text-xs'>{rarity}</Tag>
                          </div>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <span>None</span>
              )}
            </div>
          </div>
          <div>
            <div className='text-left text-sm text-white flex flex-col w-full overflow-hidden'>
              <span className='text-gray-400'>Requires 1 Shitizen to have 1 of these traits</span>{' '}
              {requirements?.traits ? (
                <div className='flex gap-x-3 gap-y-1 overflow-x-scroll mt-2 p-1 w-full'>
                  {ownedRequiredTraits.map(({ trait, owned }) => {
                    const traitData = allTraits.find((t) => t.value === trait);

                    if (!traitData) {
                      return null;
                    }

                    return (
                      <div
                        key={trait}
                        className={clsx('relative w-[75px] h-[75px] ring-2 rounded-md shrink-0', owned ? 'ring-green-400' : 'ring-red-400')}
                      >
                        <TraitImage
                          collection={getCollectionByGeneration(traitData.generation)}
                          trait={traitData.traitType}
                          value={traitData.value}
                          owned={owned}
                        />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <span>None</span>
              )}
            </div>
          </div>

          <div className='text-white text-sm text-left flex flex-col gap-1 mb-3'>
            <span className='text-gray-400'>Benefits</span>
            <div className='flex gap-3 flex-wrap'>
              <Tag>
                <span>Max Pop. increased by {upgrade.populationIncrease}</span>
              </Tag>
              <Tag>
                <span>{upgrade.name} Shitty Jobs (Coming in 2025)</span>
              </Tag>
            </div>
          </div>
        </>
      </div>
    </button>
  );
};

export default CityBuilderCityEditor;
